import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Reorder, useDragControls } from 'framer-motion';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { ArrowLeft, Check, Copy, ExternalLink, GripVertical, Pencil, Plus, Trash2 } from 'lucide-react';
import { useConfirm } from '@/shared/components/confirm';
import { PageHeader } from '@/shared/components/page-header';
import { ActionIconButton } from '@/shared/components/action-icon-button';
import { Card } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Input } from '@/shared/ui/input';
import { Skeleton } from '@/shared/ui/skeleton';
import type { NpsQuestion, NpsSurvey, SurveyExpiration } from '@/shared/types/nps';
import {
  EXPIRATION_LABEL,
  isSurveyEditable,
  isSurveyStructureEditable,
  RESPONSE_TYPE_LABEL,
  SURVEY_RESPONSE_TYPE_LABEL,
  SURVEY_STATUS_LABEL,
  SURVEY_STATUS_VARIANT,
} from '../model/nps-utils';
import { removeQuestion, reorderQuestions, updateSurvey, useNpsMe, useSurvey } from '../api/use-nps';
import { applyContentI18nUpdate } from '../model/content-i18n';
import { toSurveyPayload } from '../schemas/survey.schema';
import { QuestionEditorDialog } from './question-editor-dialog';
import { SurveySettingsFields } from '../components/survey/survey-settings-fields';
import { SurveyStatusActions } from '../components/survey/survey-status-actions';
import { PassbookIdField } from '../components/survey/passbook-id-field';
import { SurveyQrCode } from '../components/survey/survey-qr-code';
import { SurveyDuplicateDialog } from './survey-duplicate-dialog';
import { useNpsAdmin } from '../hooks/use-nps-access';

function DraggableQuestion({
  question,
  editable,
  structureEditable,
  onEdit,
  onRemove,
}: {
  question: NpsQuestion;
  editable: boolean;
  structureEditable: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const controls = useDragControls();
  const [copied, setCopied] = useState(false);
  const copyId = () => {
    navigator.clipboard?.writeText(question.id).then(() => {
      setCopied(true);
      toast.success('ID da pergunta copiado.');
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <Reorder.Item value={question} dragListener={false} dragControls={controls} className="list-none">
      <div className="glass flex items-center gap-3 rounded-2xl p-3.5">
        {structureEditable ? (
          <button
            type="button"
            aria-label="Arrastar para reordenar"
            onPointerDown={(e) => controls.start(e)}
            className="cursor-grab touch-none rounded p-3 text-muted-foreground hover:text-foreground active:cursor-grabbing"
          >
            <GripVertical className="size-4" />
          </button>
        ) : (
          <span className="size-10" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-medium">{question.title}</p>
            {question.required ? <Badge variant="secondary" className="font-normal">Obrigatória</Badge> : null}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>{RESPONSE_TYPE_LABEL[question.response_type]}</span>
            <span className="text-muted-foreground/40">·</span>
            <button type="button" onClick={copyId} title="Copiar ID" className="inline-flex items-center gap-1 rounded-md bg-muted/60 px-1.5 py-0.5 font-mono text-[10px] transition-colors hover:bg-muted">
              ID: {question.id}
              {copied ? <Check className="size-3 text-success" /> : <Copy className="size-3" />}
            </button>
          </div>
        </div>
        {editable ? (
          <div className="flex shrink-0 items-center gap-1">
            <ActionIconButton label="Editar pergunta" onClick={onEdit}><Pencil className="size-4" /></ActionIconButton>
            {structureEditable ? (
              <ActionIconButton label="Remover pergunta" onClick={onRemove} className="text-destructive"><Trash2 className="size-4" /></ActionIconButton>
            ) : null}
          </div>
        ) : null}
      </div>
    </Reorder.Item>
  );
}

export function SurveyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { data: survey, isLoading } = useSurvey(id);
  const { data: me } = useNpsMe();
  const { isAdmin } = useNpsAdmin();

  const [questions, setQuestions] = useState<NpsQuestion[]>([]);
  const [editingMeta, setEditingMeta] = useState(false);
  const [meta, setMeta] = useState<Partial<NpsSurvey>>({});
  const [savingMeta, setSavingMeta] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<NpsQuestion | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);

  const ownerPassbook = survey?.user?.legacy_user_id ?? me?.legacy_user_id ?? '';

  useEffect(() => {
    if (survey) {
      setQuestions([...(survey.questions ?? [])].sort((a, b) => a.position - b.position));
      setMeta({
        title: survey.title,
        description: survey.description ?? '',
        i18n: survey.i18n ?? {},
        response_type: survey.response_type,
        response_expiration_time: survey.response_expiration_time,
        identification_required: Boolean(survey.identification_required),
        answer_bonus: Boolean(survey.answer_bonus),
      });
    }
  }, [survey]);

  const editable = survey ? isSurveyEditable(survey.status, { isAdmin }) : false;
  const structureEditable = survey ? isSurveyStructureEditable(survey.status) : false;
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['nps', 'survey', id] });
  const shareLink = `${window.location.origin}/surveys/${id}`;

  const copyLink = () => {
    navigator.clipboard?.writeText(shareLink).then(() => {
      setCopiedLink(true);
      toast.success('Link copiado.');
      setTimeout(() => setCopiedLink(false), 1500);
    });
  };

  const copySurveyId = () => {
    if (!id) return;
    navigator.clipboard?.writeText(id).then(() => {
      setCopiedId(true);
      toast.success('ID da pesquisa copiado.');
      setTimeout(() => setCopiedId(false), 1500);
    });
  };

  const saveMeta = async () => {
    if (!id || !meta.title) return;
    setSavingMeta(true);
    try {
      if (meta.answer_bonus && meta.response_type === 'IDENTIFIED' && !ownerPassbook) {
        toast.warning('Pesquisa bonificada sem Passbook — pontos não serão creditados até vincular o ID.');
      }

      await updateSurvey(
        id,
        toSurveyPayload({
          title: meta.title,
          description: meta.description,
          i18n: meta.i18n,
          response_type: meta.response_type,
          response_expiration_time: meta.response_expiration_time,
          identification_required: meta.identification_required ?? false,
          answer_bonus: meta.answer_bonus ?? false,
        }),
      );
      toast.success('Pesquisa atualizada.');
      queryClient.invalidateQueries({ queryKey: ['nps', 'surveys'] });
      setEditingMeta(false);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao salvar.');
    } finally {
      setSavingMeta(false);
    }
  };

  const persistOrder = async (ordered: NpsQuestion[]) => {
    if (!id || !structureEditable) return;
    try {
      await reorderQuestions(id, ordered.map((q, i) => ({ id: q.id, position: i })));
    } catch {
      toast.error('Falha ao reordenar.');
      refresh();
    }
  };

  const handleRemoveQuestion = async (q: NpsQuestion) => {
    if (!structureEditable) return;
    const ok = await confirm({ title: 'Remover pergunta', description: `Remover "${q.title}"?`, confirmLabel: 'Remover', variant: 'destructive' });
    if (!ok) return;
    try {
      await removeQuestion(q.id);
      toast.success('Pergunta removida.');
      refresh();
    } catch {
      toast.error('Falha ao remover.');
    }
  };

  if (isLoading || !survey) {
    return (
      <>
        <PageHeader title="Detalhes da pesquisa" actions={<Button variant="secondary" onClick={() => navigate('/nps/pesquisas')}><ArrowLeft className="size-4" /> Voltar</Button>} />
        <div className="space-y-4"><Skeleton className="h-40 rounded-2xl" /><Skeleton className="h-64 rounded-2xl" /></div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={survey.title}
        description="Edite os dados e monte o questionário."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={SURVEY_STATUS_VARIANT[survey.status]}>{SURVEY_STATUS_LABEL[survey.status]}</Badge>
            <SurveyStatusActions survey={survey} />
            <Button variant="secondary" onClick={() => setDuplicateOpen(true)}><Copy className="size-4" /> Duplicar</Button>
            <Button variant="secondary" onClick={() => navigate('/nps/pesquisas')}><ArrowLeft className="size-4" /> Voltar</Button>
          </div>
        }
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="space-y-4 p-5 lg:col-span-1">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Configurações</h2>
            {editable && !editingMeta ? (
              <Button size="sm" variant="secondary" onClick={() => setEditingMeta(true)}><Pencil className="size-4" /> Editar</Button>
            ) : null}
          </div>

          {!editingMeta ? (
            <dl className="space-y-3 text-sm">
              <div><dt className="text-muted-foreground">Tipo</dt><dd className="font-medium">{SURVEY_RESPONSE_TYPE_LABEL[survey.response_type]}</dd></div>
              {survey.response_type === 'IDENTIFIED' ? (
                <>
                  <div><dt className="text-muted-foreground">Expiração</dt><dd className="font-medium">{survey.response_expiration_time ? EXPIRATION_LABEL[survey.response_expiration_time] : '—'}</dd></div>
                  <div><dt className="text-muted-foreground">CPF obrigatório</dt><dd className="font-medium">{survey.identification_required ? 'Sim' : 'Não'}</dd></div>
                  <div><dt className="text-muted-foreground">Bonificada</dt><dd className="font-medium">{survey.answer_bonus ? 'Sim' : 'Não'}</dd></div>
                </>
              ) : null}
              <div><dt className="text-muted-foreground">ID Passbook</dt><dd className="font-mono text-xs font-medium">{ownerPassbook || '—'}</dd></div>
              <div><dt className="text-muted-foreground">Criada em</dt><dd className="font-medium">{format(new Date(survey.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</dd></div>
            </dl>
          ) : (
            <div className="space-y-4">
              <SurveySettingsFields
                titleValue={meta.title ?? ''}
                descriptionValue={meta.description ?? ''}
                i18nValue={meta.i18n}
                onTitleChange={(v) => setMeta((m) => ({ ...m, title: v }))}
                onDescriptionChange={(v) => setMeta((m) => ({ ...m, description: v }))}
                onI18nChange={(update) =>
                  setMeta((m) => ({ ...m, i18n: applyContentI18nUpdate(m.i18n, update) }))
                }
                values={{
                  response_type: meta.response_type ?? 'ANONYMOUS',
                  identification_required: Boolean(meta.identification_required),
                  answer_bonus: Boolean(meta.answer_bonus),
                  response_expiration_time: meta.response_expiration_time as SurveyExpiration,
                }}
                onChange={(patch) => setMeta((m) => ({ ...m, ...patch }))}
              />
              <PassbookIdField
                value={ownerPassbook}
                onChange={() => {}}
                disabled
                warnBonusWithoutPassbook={Boolean(meta.answer_bonus) && meta.response_type === 'IDENTIFIED'}
              />
            </div>
          )}

          {editingMeta ? (
            <div className="flex gap-2">
              <Button variant="secondary" className="flex-1" onClick={() => setEditingMeta(false)}>Cancelar</Button>
              <Button className="flex-1" onClick={saveMeta} loading={savingMeta}>Salvar</Button>
            </div>
          ) : null}

          <div className="space-y-2 border-t border-border/50 pt-4">
            <p className="text-xs font-medium text-muted-foreground">Link público</p>
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={shareLink}
                className="h-9 min-w-0 flex-1 bg-muted/30 font-mono text-[10px]"
              />
              <Button type="button" variant="secondary" size="icon" onClick={copyLink} aria-label="Copiar link">
                {copiedLink ? <Check className="size-4 text-success" /> : <Copy className="size-4" />}
              </Button>
              <Button type="button" variant="secondary" size="icon" asChild aria-label="Abrir pesquisa">
                <a href={shareLink} target="_blank" rel="noreferrer">
                  <ExternalLink className="size-4" />
                </a>
              </Button>
            </div>
            <Button type="button" variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={copySurveyId}>
              {copiedId ? <Check className="size-3 text-success" /> : <Copy className="size-3" />}
              Copiar ID da pesquisa
            </Button>
            <div className="space-y-2 pt-2">
              <SurveyQrCode url={shareLink} logoUrl={survey.user?.profile} fileName={`qrcode-pesquisa-${survey.id}`} />
              {survey.user?.profile ? null : (
                <p className="text-xs text-muted-foreground">
                  Sem foto de perfil na conta dona da pesquisa, o QR Code sai sem imagem no centro. Envie a foto em
                  Perfil.
                </p>
              )}
            </div>
          </div>
        </Card>

        <Card className="space-y-4 p-5 lg:col-span-2">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Perguntas ({questions.length})</h2>
            {structureEditable ? (
              <Button size="sm" onClick={() => { setEditing(null); setEditorOpen(true); }}><Plus className="size-4" /> Adicionar</Button>
            ) : null}
          </div>

          {!editable ? (
            <p className="rounded-xl border border-warning/30 bg-warning/5 px-3 py-2 text-xs text-warning">
              {survey.status === 'FINISHED'
                ? 'Pesquisa finalizada — conteúdo bloqueado para edição.'
                : 'Pesquisa publicada — apenas administradores podem editar textos e traduções.'}
            </p>
          ) : !structureEditable ? (
            <p className="rounded-xl border border-border/60 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
              Pesquisa publicada — você pode editar textos e traduções. Adicionar ou remover perguntas só em rascunho.
            </p>
          ) : null}

          {questions.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Nenhuma pergunta ainda.</p>
          ) : (
            <Reorder.Group
              as="ul"
              axis="y"
              values={questions}
              onReorder={structureEditable ? setQuestions : () => {}}
              className="space-y-2.5 p-0"
            >
              {questions.map((q) => (
                <DraggableQuestion
                  key={q.id}
                  question={q}
                  editable={editable}
                  structureEditable={structureEditable}
                  onEdit={() => { setEditing(q); setEditorOpen(true); }}
                  onRemove={() => handleRemoveQuestion(q)}
                />
              ))}
            </Reorder.Group>
          )}
          {structureEditable && questions.length > 1 ? (
            <Button variant="secondary" size="sm" onClick={() => persistOrder(questions)} className="w-full">Salvar ordem</Button>
          ) : null}
        </Card>
      </div>

      <QuestionEditorDialog
        surveyId={id ?? ''}
        question={editing}
        open={editorOpen}
        disabled={!editable}
        onOpenChange={(open) => {
          setEditorOpen(open);
          if (!open) setEditing(null);
        }}
        onSaved={refresh}
      />

      <SurveyDuplicateDialog
        surveyId={duplicateOpen ? survey.id : null}
        onClose={() => setDuplicateOpen(false)}
        onDuplicated={(newId) => {
          setDuplicateOpen(false);
          navigate(`/nps/pesquisas/${newId}`);
        }}
      />
    </>
  );
}
